import type {
    ChatSender,
    DeliveryStatus,
} from "@albertine/design-system/components/chat";

/** Same-sender messages stay in one cluster only while consecutive gaps and
 *  total cluster span stay under this window (iMessage / Slack-style). */
export const MESSAGE_CLUSTER_WINDOW_MS = 10 * 60 * 1000;

type ClusterMessage = {
    sender: ChatSender;
    sentAt: Date;
    deliveryStatus?: DeliveryStatus;
};

export function shouldShowSenderRow(
    current: ClusterMessage,
    next: ClusterMessage | undefined,
    clusterStartSentAt: Date,
): boolean {
    if (current.deliveryStatus === "sending" || current.deliveryStatus === "failed") {
        return true;
    }
    if (!next || next.sender !== current.sender) {
        return true;
    }

    const nextTime = next.sentAt.getTime();
    const gapFromCurrent = Math.abs(nextTime - current.sentAt.getTime());
    if (gapFromCurrent >= MESSAGE_CLUSTER_WINDOW_MS) {
        return true;
    }

    // Prevent long chains where every consecutive pair is close but the
    // cluster as a whole drifts far from the first message's time.
    const spanWithNext = Math.abs(nextTime - clusterStartSentAt.getTime());
    return spanWithNext >= MESSAGE_CLUSTER_WINDOW_MS;
}

export function annotateThreadClusters<
    T extends {
        sentAt: Date;
        message: { sender: ChatSender; deliveryStatus?: DeliveryStatus };
    },
>(
    items: T[],
): Array<
    T & {
        showSenderRow: boolean;
        message: T["message"] & { showSenderRow: boolean };
    }
> {
    let clusterStartSentAt = items[0]?.sentAt;

    return items.map((item, index) => {
        const next = items[index + 1];
        const showSenderRow = shouldShowSenderRow(
            {
                sender: item.message.sender,
                sentAt: item.sentAt,
                deliveryStatus: item.message.deliveryStatus,
            },
            next
                ? {
                      sender: next.message.sender,
                      sentAt: next.sentAt,
                      deliveryStatus: next.message.deliveryStatus,
                  }
                : undefined,
            clusterStartSentAt ?? item.sentAt,
        );

        if (showSenderRow) {
            clusterStartSentAt = next?.sentAt;
        }

        return {
            ...item,
            showSenderRow,
            message: {
                ...item.message,
                showSenderRow,
            },
        };
    });
}

export function groupIntoSenderClusters<T extends { showSenderRow: boolean }>(
    items: T[],
): T[][] {
    const clusters: T[][] = [];
    let current: T[] = [];

    for (const item of items) {
        current.push(item);
        if (item.showSenderRow) {
            clusters.push(current);
            current = [];
        }
    }

    if (current.length > 0) {
        clusters.push(current);
    }

    return clusters;
}
